// generic chunked-upload helper for the Word RA path (and any future
// upload that needs to dodge Cloudflare Free's 100 s edge timeout).
//
// mirrors the chunk-assembly pattern from releaseDownload.ts but in
// the upload direction. the three URLs map onto the three server
// endpoints added in Phase A2 (init / chunk / complete). concurrency
// defaults to 2, empirically safe through CF without tripping rate
// limiting on the free plan. chunkSize defaults to 1 MB so each
// request finishes in <1 s on a slow consumer link.
//
// retry policy: each chunk retried up to 3 times with a short
// backoff. if a chunk hard-fails (network gone, server 5xx after
// retries), the whole upload aborts and the server-side session is
// left to TTL-expire, the caller just starts over.

import { API_BASE_URL, fetchWithAuthRetry } from './apiClient'

export interface ChunkedUploadOpts {
  file:        File
  initUrl:     string                                 // path beginning with /api/...
  chunkUrl:    string
  completeUrl: string
  chunkSize?:  number                                 // bytes, default 1 MB
  concurrency?: number                                // default 2
  onProgress?: (loaded: number, total: number) => void
}

interface InitResponse {
  session_id:         string
  expires_in_seconds: number
  total_chunks:       number
}

const DEFAULT_CHUNK_SIZE  = 1 * 1024 * 1024
// Cloudflare Free's edge sporadically returns 502 under concurrent
// multipart POSTs from the same client IP. Phase A2's retry logic
// absorbed it but the UX was choppy (~32% chunk-level no-response
// rate per recon). single-thread is reliable and only ~2× slower
// in wall-clock for the 33 MB SN2729 fixture (~30s vs ~15s),
// acceptable tradeoff until CF Pro or a different ingest path.
//
// configurable via VITE_CHUNK_CONCURRENCY for an off-hours prod
// A/B test if CF-502 health proves stable. default stays 1, a
// build-time env override is enough, no runtime config needed
// since the value is read once per page load. non-numeric or
// non-positive values fall back to 1 so a typo in .env can't put
// the upload path into an obviously-broken state.
const ENV_CONCURRENCY     = Number(import.meta.env.VITE_CHUNK_CONCURRENCY)
const DEFAULT_CONCURRENCY = Number.isFinite(ENV_CONCURRENCY) && ENV_CONCURRENCY > 0
  ? Math.floor(ENV_CONCURRENCY)
  : 1
const CHUNK_RETRIES       = 3
const RETRY_BACKOFF_MS    = 600

function url(path: string): string {
  return path.startsWith('http') ? path : `${API_BASE_URL}${path}`
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchWithAuthRetry(url(path), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error ?? `request failed: ${res.status}`)
  }
  return res.json() as Promise<T>
}

async function postChunk(
  path:        string,
  session_id:  string,
  chunk_index: number,
  blob:        Blob,
): Promise<void> {
  const form = new FormData()
  form.append('session_id',  session_id)
  form.append('chunk_index', String(chunk_index))
  form.append('chunk',       blob, `chunk-${chunk_index}`)

  let lastErr: Error | null = null
  for (let attempt = 0; attempt < CHUNK_RETRIES; attempt++) {
    try {
      const res = await fetchWithAuthRetry(url(path), { method: 'POST', body: form })
      if (res.ok) return
      const err = await res.json().catch(() => ({ error: res.statusText }))
      lastErr = new Error(err.error ?? `chunk ${chunk_index} failed: ${res.status}`)
      // don't retry on 4xx, client errors won't get better with a retry
      if (res.status >= 400 && res.status < 500) throw lastErr
    } catch (err: any) {
      lastErr = err instanceof Error ? err : new Error(String(err))
    }
    if (attempt < CHUNK_RETRIES - 1) {
      await new Promise(r => setTimeout(r, RETRY_BACKOFF_MS * (attempt + 1)))
    }
  }
  throw lastErr ?? new Error(`chunk ${chunk_index}: unknown failure`)
}

export async function chunkedUpload<TResult>(opts: ChunkedUploadOpts): Promise<TResult> {
  const chunkSize   = opts.chunkSize   ?? DEFAULT_CHUNK_SIZE
  const concurrency = opts.concurrency ?? DEFAULT_CONCURRENCY
  const file        = opts.file
  const total_chunks = Math.ceil(file.size / chunkSize)

  // 1) init
  const init = await postJson<InitResponse>(opts.initUrl, {
    filename:     file.name,
    total_chunks,
  })

  // 2) chunks: worker pool of `concurrency` parallel uploads
  let nextIdx = 0
  let uploaded = 0
  opts.onProgress?.(0, file.size)

  const worker = async () => {
    while (true) {
      const i = nextIdx++
      if (i >= total_chunks) return
      const start = i * chunkSize
      const end   = Math.min(start + chunkSize, file.size)
      const blob  = file.slice(start, end)
      await postChunk(opts.chunkUrl, init.session_id, i, blob)
      uploaded += (end - start)
      opts.onProgress?.(uploaded, file.size)
    }
  }
  const workers = Array.from({ length: Math.max(1, concurrency) }, () => worker())
  // if any worker throws, surface the first error. other workers
  // aren't actively cancelled, they finish their current request and
  // exit because `nextIdx` won't reset. the in-flight chunks just
  // feed a now-discarded server session.
  await Promise.all(workers)

  // 3) complete
  return await postJson<TResult>(opts.completeUrl, { session_id: init.session_id })
}
